import { useState } from "react";
import { Currency } from "../../../constants";

interface LocalFeeDisplayProps {
  amount: string;
  currency: Currency;
  fee: number;
  isLoading: boolean;
}

export function LocalFeeDisplay({
  amount,
  currency,
  fee,
  isLoading,
}: LocalFeeDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const numericAmount = Number(amount);
  const totalDebit = numericAmount + fee;

  const formattedAmount = numericAmount.toLocaleString("en-US");
  const formattedFee = fee.toLocaleString("en-US");
  const formattedTotal = totalDebit.toLocaleString("en-US");

  if (!amount || numericAmount === 0) {
    return null;
  }

  return (
    <div className="local-fee-display bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <svg
            className="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <span className="text-sm font-medium text-gray-700">
            Transaction Summary
          </span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3 text-sm">
          {/* Withdrawal Amount */}
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Withdrawal amount</span>
            <span className="font-semibold text-gray-900">
              {formattedAmount} {currency}
            </span>
          </div>

          {/* Transaction Fee */}
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Transaction fee</span>
            {isLoading ? (
              <div className="h-5 w-20 bg-gray-200 animate-pulse rounded" />
            ) : (
              <span className="text-red-600 font-medium">
                + {formattedFee} {currency}
              </span>
            )}
          </div>

          {/* Divider */}
          <div className="border-t-2 border-primary pt-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-semibold">
                Total debit from wallet
              </span>
              {isLoading ? (
                <div className="h-6 w-24 bg-gray-200 animate-pulse rounded" />
              ) : (
                <span className="text-lg font-bold text-primary">
                  {formattedTotal} {currency}
                </span>
              )}
            </div>
          </div>

          {/* Info note */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-3">
            <div className="flex items-center space-x-2">
              <svg
                className="w-4 h-4 text-gray-600 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-xs text-gray-700">
                The recipient receives {formattedAmount} {currency}. The fee
                will be deducted from your wallet.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
